// src/components/account/SsoLoginPage.tsx
// Handles SSO login from the CRM: reads ?token= from URL, validates it,
// and auto-logs the user in using their CPF / email as identifier.

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CACHE_KEYS, CACHE_OPTIONS } from "../../constants/cache-keys";
import { useAccount } from "../../context";
import { CacheManager } from "../../utils/cache-manager";

interface JwtPayload {
  sub?: string;
  identifier?: string; // CPF or email
  email?: string;
  name?: string;
  exp?: number;
  aud?: string;
  iss?: string;
}

/** Decode a JWT payload without verifying signature (verification happens server-side at login). */
function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const raw = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(
      raw.padEnd(raw.length + ((4 - (raw.length % 4)) % 4), "="),
    );
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

type Status = "loading" | "error" | "success";

const SsoLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { setAccount } = useAccount();
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState("Autenticando via SSO...");
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");

      if (!token) {
        setStatus("error");
        setMessage("Token SSO não encontrado na URL.");
        return;
      }

      const payload = decodeJwtPayload(token);
      if (!payload) {
        setStatus("error");
        setMessage("Token SSO inválido.");
        return;
      }

      // Check expiry client-side (server also validates)
      if (payload.exp && Date.now() / 1000 > payload.exp) {
        setStatus("error");
        setMessage("Token SSO expirado. Por favor, acesse o CRM novamente.");
        return;
      }

      const identifier = payload.identifier || payload.email;
      if (!identifier) {
        setStatus("error");
        setMessage(
          "Identificador de usuário (CPF/e-mail) não encontrado no token.",
        );
        return;
      }

      try {
        // Exchange the SSO token for WPP session cookies via the dedicated endpoint.
        // The WPP backend validates the token (issuer, audience, expiry) and sets
        // httpOnly cookie-based session — same flow as a normal login.
        const backendUrl = import.meta.env.DEV
          ? ""
          : import.meta.env.VITE_BACKEND_URL || "";
        const res = await fetch(`${backendUrl}/api/auth/sso-login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ sso_token: token }),
        });

        const json = await res.json();

        if (!res.ok || json.status !== "success") {
          throw new Error(json.message || "Falha no login SSO");
        }

        // Salva o token no localStorage para ser usado pelo axios, contornando problemas de cookies SameSite
        if (json.data?.token) {
          localStorage.setItem("wpp_access_token", json.data.token);
        }

        // Salva o company_id para resolver conflitos de Multi-Tenant no axios
        if (json.data?.user?.company_id) {
          localStorage.setItem("wpp_company_id", json.data.user.company_id);
        }

        // Inject the user object directly so the app doesn't think we are logged out after redirect
        if (json.data?.user) {
          setAccount(json.data.user);
        }

        // Mark auth state so AccountProvider picks it up
        CacheManager.set(
          CACHE_KEYS.AUTH.STATE,
          "login",
          CACHE_OPTIONS.AUTH.STATE,
        );

        setStatus("success");
        setMessage("Autenticado! Redirecionando...");

        // Small delay so user sees the success state, then go home
        setTimeout(() => navigate("/", { replace: true }), 800);
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : "Erro ao autenticar via SSO.";
        setStatus("error");
        setMessage(msg);
      }
    };

    run();
  }, [navigate]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        color: "#f1f5f9",
        fontFamily: "Inter, system-ui, sans-serif",
        gap: "1.5rem",
      }}
    >
      {/* Logo / Branding */}
      <div
        style={{ fontSize: "2.5rem", fontWeight: 700, letterSpacing: "-1px" }}
      >
        💬 Virtus WhatsApp
      </div>

      {/* Status card */}
      <div
        style={{
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: "1rem",
          padding: "2rem 2.5rem",
          textAlign: "center",
          minWidth: "320px",
          backdropFilter: "blur(12px)",
        }}
      >
        {status === "loading" && (
          <>
            <div
              style={{
                width: 48,
                height: 48,
                border: "4px solid rgba(99,102,241,0.3)",
                borderTopColor: "#6366f1",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                margin: "0 auto 1rem",
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {status === "success" && (
          <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>✅</div>
        )}

        {status === "error" && (
          <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>❌</div>
        )}

        <p
          style={{
            margin: 0,
            fontSize: "1rem",
            color: status === "error" ? "#f87171" : "#e2e8f0",
          }}
        >
          {message}
        </p>

        {status === "error" && (
          <button
            onClick={() => navigate("/", { replace: true })}
            style={{
              marginTop: "1.5rem",
              padding: "0.6rem 1.5rem",
              borderRadius: "0.5rem",
              background: "#6366f1",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "0.9rem",
            }}
          >
            Ir para o login
          </button>
        )}
      </div>
    </div>
  );
};

export default SsoLoginPage;
